<template>
  <div
    :class="{
      'mt-2': true,
      'wrapper': !isEdit
    }">

    <f-alert
      v-if="!isEdit && this.userType === 'adviser' && !isCustomerSelected"
      class="my-3"
      theme="warning"
      visible>
      {{ $t('inquiryForm.chooseTheCustomer') }}
    </f-alert>

    <div
      v-if="!allDataLoaded"
      class="loading-spinner-wrapper">
      <div class="loading-spinner pb-3 pt-3">
        <span class="loading-spinner-content">
          {{ $t('ui.loadingDataInProgress') }}&hellip;
        </span>
      </div>
    </div>

    <validation-observer
      ref="inquiry-form-observer"
      tag="form"
      :class="{
        'form-readonly': readonly
      }">
      <f-tabs
        v-if="!(!isEdit && !isCustomerSelected)"
        ref="inquiry-tabs"
        :items="tabItems"
        :tabsVertical="true"
        :readonly="readonly" >
        <div slot="tab-0">
          <inquiry-transaction-type-form
            ref="inquiry-transaction-type-form"
            v-if="allDataLoaded"
            :isEdit="isEdit"
            :index="0"
            :readonly="readonly"
            :isDuplicate="isDuplicate"
            :isValid="validationResults.indexOf('transaction-type') === -1"
            :propertyValue="data.property_value"
            :repaymentPeriod="data.repayment_period"
            :ownContribution="data.own_contribution"
            :expectedLoanInstallment="data.expected_loan_installment"
            :transactionTypeID="data.transaction_type_id"
            :transactionSubjectID="data.transaction_subject_id"
            :measurementMeters="data.measurement"
            :isNeedFinishing="data.is_need_finishing"
            :needFinishingAmount="data.need_finishing_amount"
            :transactionAdditionalInformation="data.transaction_additional_information"/>
        </div>
        <div slot="tab-1">
          <inquiry-contact-info-form
            ref="inquiry-contact-info-form"
            v-if="allDataLoaded"
            :isEdit="isEdit"
            :index="1"
            :readonly="readonly"
            :isDuplicate="isDuplicate"
            :isValid="validationResults.indexOf('contact-info') === -1"
            :customerID="data.customer_id"
            :customerName="data.customer_name"
            :customerSurname="data.customer_surname"
            :customerEmail="data.customer_email"
            :customerPhone="data.customer_phone"
            :customerPostCode="data.customer_post_code"
            :customerCity="data.customer_city"
            :customerContactHours="data.customer_contact_hours"
            :customerLanguageId="data.customer_language_id"
            :isCustomerInvited="!data.can_add_message"
            :userType="userType"/>
        </div>
        <div slot="tab-2">
          <inquiry-financial-info-form
            ref="inquiry-financial-info-form"
            v-if="allDataLoaded"
            :isEdit="isEdit"
            :index="2"
            :readonly="readonly"
            :isDuplicate="isDuplicate"
            :incomes="data.incomes"
            :allLiabilities="allLiabilities"
            :allBanks="allBanks"
            :allCurrencies="allCurrencies" />
        </div>
        <div slot="tab-3">
          <inquiry-household-info-form
            ref="inquiry-household-info-form"
            v-if="allDataLoaded"
            :isEdit="isEdit"
            :index="3"
            :readonly="readonly"
            :isValid="validationResults.indexOf('household-info') === -1"
            :flatmate_count="data.flatmate_count"
            :credit_recipient_age="parseInt(data.credit_recipient_age, 10)"
            :household_additional_information="data.household_additional_information" />
        </div>
       <div slot="tab-4">
          <inquiry-liabilities-info-form
            ref="inquiry-liabilities-info-form"
            v-if="allDataLoaded"
            :isEdit="isEdit"
            :index="4"
            :readonly="readonly"
            :isDuplicate="isDuplicate"
            :liabilities="data.liabilities"
            :allLiabilities="allLiabilities"
            :allBanks="allBanks"
            :allCurrencies="allCurrencies" />
        </div>
        <div slot="tab-5">
          <inquiry-bank-offer-form
            ref="inquiry-bank-offer-form"
            v-if="allDataLoaded"
            :isEdit="isEdit"
            :index="5"
            :readonly="readonly"
            :isDuplicate="isDuplicate"
            :isValid="validationResults.indexOf('bank-offer') === -1"
            :selectedBanks="data.selected_banks"
            :banks="data.banks" />
        </div>
      </f-tabs>
    </validation-observer>

    <inquiry-liability-popup />
    <inquiry-income-popup />
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import InquiryTransactionType from './InquiryTransactionType.vue';
import InquiryContactInfo from './InquiryContactInfo.vue';
import InquiryFinancialInfo from './InquiryFinancialInfo.vue';
import InquiryHouseholdInfo from './InquiryHouseholdInfo.vue';
import InquiryLiabilitiesInfo from './InquiryLiabilitiesInfo.vue';
import InquiryBankOffer from './InquiryBankOffer.vue';
import InquiryLiabilityPopup from '@/components/popups/InquiryLiabilityPopup.vue';
import InquiryIncomePopup from '@/components/popups/InquiryIncomePopup.vue';

export default {
  name: 'inquiry-form',
  components: {
    'inquiry-transaction-type-form': InquiryTransactionType,
    'inquiry-contact-info-form': InquiryContactInfo,
    'inquiry-financial-info-form': InquiryFinancialInfo,
    'inquiry-household-info-form': InquiryHouseholdInfo,
    'inquiry-liabilities-info-form': InquiryLiabilitiesInfo,
    'inquiry-bank-offer-form': InquiryBankOffer,
    'inquiry-liability-popup': InquiryLiabilityPopup,
    'inquiry-income-popup': InquiryIncomePopup
  },
  props: {
    index: {
      type: Number,
      required: true
    },
    readonly: {
      type: Boolean,
      required: true
    },
    isDuplicate: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    allDataLoaded (newValue) {
      if (!newValue || this.userType === 'adviser') {
        return;
      }

      Vue.nextTick(() => {
        this.setCustomer(this.currentCustomer);
      });
    }
  },
  computed: {
    allDataLoaded () {
      return (this.userType === 'adviser' ? this.customersLoaded : this.customerLoaded) && this.currenciesLoaded && this.liabilitiesLoaded && this.banksLoaded && this.loaded;
    },
    isEdit () {
      return !!this.$route.params.id;
    },
    tabItems () {
      return [
        {
          title: this.$t('inquiryForm.basicInfo'),
          completed: false,
          formName: 'transaction-type'
        },
        {
          title: this.$t('inquiryContactInfo.contactInformation'),
          completed: false,
          formName: 'contact-info'
        },
        {
          title: this.$t('inquiryFinancialInfo.financialData'),
          completed: false,
          formName: 'incomes-info'
        },
        {
          title: this.$t('inquiryForm.householdInfo'),
          completed: false,
          formName: 'household-info'
        },
        {
          title: this.$t('inquiryFinancialInfo.financialLiabilities'),
          completed: false,
          formName: 'liabilities-info'
        },
        {
          title: this.$t('inquiryBankOffer.preferredBanksOffer'),
          completed: false,
          formName: 'bank-offer'
        }
      ]
    },
    validationResults () {
      let errors = this.$refs['inquiry-form-observer'].errors;
      let allKeys = Object.keys(errors);
      let errorKeys = [];
      let errorTabs = [];
      let results = [
        ['transaction-type', ['property-value', 'repayment-period', 'own-contribution', 'expected-loan-installment', 'transaction-type', 'transaction-subject', 'need-finishing-amount', 'measurement']],
        ['contact-info', ['customer-name', 'customer-surname', 'customer-email', 'customer-phone', 'customer-postal-code', 'customer-city']],
        ['household-info', ['flatmate-count', 'credit-recipient-age']],
        ['bank-offer', ['selected-banks']]
      ];

      for (let i = 0; i < allKeys.length; i++) {
        if (errors[allKeys[i]].length) {
          errorKeys.push(allKeys[i]);
        }
      }

      if (
        this.$refs['inquiry-liabilities-info-form'] &&
        !this.$refs['inquiry-liabilities-info-form'].liabilities.length &&
        !this.$refs['inquiry-liabilities-info-form'].isFormComplete
      ) {
        errorTabs.push('liabilities-info');
      }

      if (
        this.$refs['inquiry-financial-info-form'] &&
        !this.$refs['inquiry-financial-info-form'].incomes.length &&
        !this.$refs['inquiry-financial-info-form'].isFormComplete
      ) {
        errorTabs.push('incomes-info');
      }

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errorKeys.indexOf(key) > -1) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs;
    }
  },
  data() {
    return {
      data: {
        id: 0,
        adviser_id: 0,
        adviser_name: '',
        adviser_surname: '',
        customer: null,
        customer_id: 0,
        customer_name: '',
        customer_surname: '',
        customer_email: '',
        customer_phone: '',
        customer_post_code: '',
        customer_city: '',
        customer_contact_hours: null,
        customer_language_id: 0,
        status: '',
        property_value: 0,
        repayment_period: 0,
        own_contribution: 0,
        expected_loan_installment: 0,
        files: [],
        transaction_type_id: 0,
        transaction_subject_id: 0,
        measurement: 0,
        is_need_finishing: false,
        need_finishing_amount: 0,
        transaction_additional_information: '',
        flatmate_count: 1,
        credit_recipient_age: 0,
        household_additional_information: '',
        selected_banks: false,
        banks: [],
        liabilities: [],
        incomes: [],
        can_add_message: false
      },
      userType: null,
      loaded: false,
      loadError: false,
      allCustomers: [],
      customersLoaded: false,
      allProvinces: [],
      provincesLoaded: false,
      currentCustomer: null,
      customerLoaded: false,
      isLoading: false,
      loadedTabArray: [],
      isCustomerSelected: false,
      allCurrencies: [],
      currenciesLoaded: false,
      allLiabilities: [],
      liabilitiesLoaded: false,
      allBanks: [],
      banksLoaded: false
    };
  },
  mounted () {
    this.userType = this.$store.getters['getUserType'].toLowerCase();
    this.$bus.$on('inquiry-form-part-complete', this.setFormTypeComplete);
    this.$bus.$on('inquiry-form-go-next-tab', this.goNextTab);
    this.$bus.$on('inquiry-form-submit', this.validateForm);
    this.$bus.$on('inquiry-customer-selected', this.setCustomer);

    if (this.isEdit || this.isDuplicate) {
      this.isLoading = true;
      this.loadedTabArray = [];
    }

    this.loadAdditionalData();

    if (!this.isEdit && !this.isDuplicate) {
      this.loaded = true;
    }
  },
  methods: {
    setInquiry (inquiry) {
      Vue.set(this, 'data', JSON.parse(JSON.stringify(inquiry)));
      this.loaded = true;
    },
    submitForm () {
      if (this.validationResults.length) {
        return;
      }

      if (this.userType !== 'customer' && (!this.data.customer || !this.data.customer.id)) {
        return;
      }

      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit ? '/api/' + this.userType + '/inquiries/update/' + this.data.id : '/api/' + this.userType + '/inquiries/store';

      let successTitleTxt = this.$t('inquiryForm.saveOffer');
      let successTextTxt = this.$t('inquiryForm.offerHasBeenSave');
      let errorTextTxt = this.$t('inquiryForm.modifyOfferError')

      if (this.$store.getters['getUserType'].toLowerCase() === 'customer') {
        if (!this.data.adviser_id) {
          successTitleTxt = this.$t('inquiryForm.formSaved');
          successTextTxt = this.$t('inquiryForm.thankYouForFillingForm');
          errorTextTxt = this.$t('inquiryForm.modifyFormError')
        } else if (!this.data.files.length) {
          successTitleTxt = this.$t('inquiryForm.querySaved');
          successTextTxt = this.$t('inquiryForm.queryHasBeenSaved');
          errorTextTxt = this.$t('inquiryForm.queryModifyError')
        }
      }

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: successTitleTxt,
        successText: successTextTxt,
        errorTitle: 'Wystąpił błąd',
        errorText: errorTextTxt,
        successAction: () => {
          this.$router.push('/oferty-kredytowe');
        }
      });
    },
    prepareFormDataToSend () {
      let formDataToSend = {};
      let transactionInfo = this.$refs['inquiry-transaction-type-form'].getData();
      let contactInfo = this.$refs['inquiry-contact-info-form'].getData();
      let financialInfo = this.$refs['inquiry-financial-info-form'].getData();
      let householdInfo = this.$refs['inquiry-household-info-form'].getData();
      let liabilitiesInfo = this.$refs['inquiry-liabilities-info-form'].getData();
      let bankOffer = this.$refs['inquiry-bank-offer-form'].getData();
      Object.assign(formDataToSend, transactionInfo, contactInfo, financialInfo, householdInfo, liabilitiesInfo, bankOffer);

      if (this.userType !== 'customer') {
        formDataToSend.customer_id = this.data.customer.id;
      } else {
        formDataToSend.customer_id = this.currentCustomer.id;
      }

      return formDataToSend;
    },
    validateForm () {
      FormUtils.validate(this.$refs['inquiry-form-observer'], this.submitForm, this.setFormTypeInvalid);
    },
    loadAdditionalData () {
      if (this.userType === 'adviser') {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/api/adviser/customers/items',
          method: 'get',
          outputKey: 'allCustomers',
          loadedKey: 'customersLoaded',
          errorKey: 'loadError',
          params: {
            pagination: 0
          }
        });
      } else {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/api/customer/customer/item',
          method: 'get',
          outputKey: 'currentCustomer',
          loadedKey: 'customerLoaded',
          errorKey: 'loadError',
          params: {
            pagination: 0
          }
        });
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/currencies/items',
        method: 'get',
        outputKey: 'allCurrencies',
        loadedKey: 'currenciesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/liabilities/items',
        method: 'get',
        outputKey: 'allLiabilities',
        loadedKey: 'liabilitiesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/banks/items',
        method: 'get',
        outputKey: 'allBanks',
        loadedKey: 'banksLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    setFormTypeComplete (index, formPartIsComplete, formPartName) {
      let isComplete = formPartIsComplete && this.validationResults.indexOf(formPartName) < 0;

      if (this.isLoading) {
        if (this.loadedTabArray.indexOf(formPartName) === -1) {
          this.loadedTabArray.push(formPartName);
        }

        if (this.loadedTabArray.length === 6) {
          this.isLoading = false;
        }
      }

      this.$refs['inquiry-tabs'].setItemCompleted(index, isComplete);
      let notCompletedFormParts = this.tabItems.filter(item => item.completed === false);
      if (!this.isLoading) {
        this.$bus.$emit('inquiry-main-part-complete', this.index, !(notCompletedFormParts.length > 0));
      }
    },
    setFormTypeInvalid () {
      this.validationResults.forEach(item => {
        let itemIndex = this.tabItems.indexOf(this.tabItems.find(ele => ele.formName === item));
        this.$refs['inquiry-tabs'].setItemInvalid(itemIndex, true);
      });
    },
    goNextTab (index) {
      if (index > this.tabItems.length) {
        this.validateForm();
      } else {
        this.$refs['inquiry-tabs'].toggle(this.tabItems[index]);
      }
    },
    setCustomer (selectedCustomer) {
      if (!this.readonly || this.isDuplicate) {
        Vue.set(this, 'isCustomerSelected', !!selectedCustomer);
        Vue.nextTick(() => {
          if (this.isCustomerSelected) {
            Vue.set(this.data, 'customer', JSON.parse(JSON.stringify(selectedCustomer)));
            this.$refs['inquiry-contact-info-form'].setCustomerData(selectedCustomer);
          }
        });
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('inquiry-form-part-complete', this.setFormTypeComplete);
    this.$bus.$off('inquiry-form-go-next-tab', this.goNextTab);
    this.$bus.$off('inquiry-form-submit', this.validateForm);
    this.$bus.$off('inquiry-customer-selected', this.setCustomer);
  }
}
</script>
